<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref } from 'vue'
  import {
    AiAugmentedByMapperKeysEnum as DatamapperKey,
    type CustomerCase as Case
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import { CardContent, Card, CardTitle } from '@/components/ui/card'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import Button from '@/components/ui/button/Button.vue'
  import ScrollArea from '@/components/ui/scroll-area/ScrollArea.vue'
  import { Loader2 } from 'lucide-vue-next'
  import { useToast } from '@/components/ui/toast'
  import { storeToRefs } from 'pinia'
  import { normalizeToIsoDate } from '@/lib/util/dateUtils'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const emit = defineEmits(['close'])

  // Translations
  const { t } = useTranslation({
    en: {
      dates: 'Dates',
      repairCosts: 'Repair Costs',
      values: 'Values',
      tax: 'Tax',
      results: 'Results',
      accidentDate: 'Accident Date',
      expertDate: 'Expert Report Date',
      netRepairCosts: 'Net Repair Costs',
      grossRepairCosts: 'Gross Repair Costs',
      repairDuration: 'Repair Duration',
      valueLoss: 'Value Loss',
      netReplacementValue: 'Net Replacement Value',
      grossReplacementValue: 'Gross Replacement Value',
      grossResidualValue: 'Gross Residual Value',
      netGrossReplacementCosts: 'Net-Gross Replacement Costs',
      grossGrossReplacementCosts: 'Gross-Gross Replacement Costs',
      differentialTax: 'Differential Tax',
      regularTax: 'Regular Tax',
      damageAssessmentTime: 'Damage Assessment Time',
      considerationPeriod: 'Consideration Period',
      dayOrDays: 'Day(s)',
      days: 'days',
      compensationForLossOfUse: 'Compensation for Loss of Use',
      save: 'Save',
      updatedFields: 'Updated Fields'
    },
    de: {
      dates: 'Termine',
      repairCosts: 'Reparaturkosten',
      values: 'Werte',
      tax: 'Steuer',
      results: 'Ergebnisse',
      accidentDate: 'Unfalldatum',
      expertDate: 'Datum Gutachten',
      netRepairCosts: 'Nettoreparaturkosten',
      grossRepairCosts: 'Bruttoreparaturkosten',
      repairDuration: 'Reparaturdauer',
      valueLoss: 'Wertminderung',
      netReplacementValue: 'Nettowiederbeschaffungswert',
      grossReplacementValue: 'Bruttowiederbeschaffungswert',
      grossResidualValue: 'Bruttorestwert',
      netGrossReplacementCosts: 'Netto-Brutto-Wiederbeschaffungsaufwand',
      grossGrossReplacementCosts: 'Brutto-Brutto-Wiederbeschaffungsaufwand',
      differentialTax: 'Differenzsteuer',
      regularTax: 'Regelsteuer',
      damageAssessmentTime: 'Schadensermittlungszeit',
      considerationPeriod: 'Überlegungsfrist',
      dayOrDays: 'Tag(e)',
      days: 'Tage',
      compensationForLossOfUse: 'Nutzungsausfallentschädigung',
      save: 'Speichern',
      updatedFields: 'Felder aktualisiert'
    }
  })

  const caseStore = useCaseStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const { isSavingCase } = storeToRefs(caseStore)
  const { trackEvent } = useMetrics()

  // Input fields with store bindings
  const accidentDate = ref<string>('')
  const expertDate = ref<string>('')
  const repairDuration = ref<number>(0)

  // Numeric input fields
  const repairCostsWithVat = ref<number>(0)
  const repairCostsWithoutVat = computed(() => {
    return repairCostsWithVat.value / 1.19
  })

  // This section was intentionally left empty as the netReplacementValue is already defined below

  const merantileValueLoss = ref<number>(0)
  const grossReplacementValue = ref<number>(0)
  const grossResidualValue = ref<number>(0)

  // Tax options
  const isDifferenzsteuer = ref<boolean>(false)
  const isRegelsteuer = ref<boolean>(false)

  // Computed values
  const netReplacementValue = computed(() => {
    if (isDifferenzsteuer.value) {
      return grossReplacementValue.value / 1.025
    } else if (isRegelsteuer.value) {
      return grossReplacementValue.value / 1.19
    }
    return grossReplacementValue.value
  })

  // Calculate replacement costs
  const netGrossReplacementCosts = computed(() => {
    return netReplacementValue.value - grossResidualValue.value
  })

  const grossGrossReplacementCosts = computed(() => {
    return grossReplacementValue.value - grossResidualValue.value
  })

  const loadInitialData = async () => {
    // Load date values
    const accidentDateFromBackend =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_ACCIDENT)) || ''
    accidentDate.value = normalizeToIsoDate(accidentDateFromBackend)

    const expertDateFromBackend =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_REPORT)) || ''
    expertDate.value = normalizeToIsoDate(expertDateFromBackend)

    // Load tax options
    isDifferenzsteuer.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX)) ===
      true
    isRegelsteuer.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX)) === true

    // Load numeric values
    const repairCostsWithVatValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT
    )
    repairCostsWithVat.value = repairCostsWithVatValue ? parseFloat(repairCostsWithVatValue) : 0

    const depreciationValueValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS
    )
    merantileValueLoss.value = depreciationValueValue ? parseFloat(depreciationValueValue) : 0

    const replacementValueGrossValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE
    )
    grossReplacementValue.value = replacementValueGrossValue
      ? parseFloat(replacementValueGrossValue)
      : 0

    const residualValueGrossValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE
    )
    grossResidualValue.value = residualValueGrossValue ? parseFloat(residualValueGrossValue) : 0

    const repairDurationValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION
    )
    repairDuration.value = repairDurationValue ? parseFloat(repairDurationValue) : 0
  }

  // Call loadInitialData when component is mounted
  loadInitialData()

  // Format function for read-only fields
  const formatToTwoDecimals = (value: number | string | null | undefined): string => {
    // Ensure value is a number before using toFixed
    const numValue = typeof value === 'number' ? value : Number(value || 0)
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2)
  }

  // Format computed values for display in read-only fields
  const formattedRepairCostsWithoutVat = computed(() =>
    formatToTwoDecimals(repairCostsWithoutVat.value)
  )
  const formattedNetReplacementValue = computed(() =>
    formatToTwoDecimals(netReplacementValue.value)
  )
  const formattedNetGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(netGrossReplacementCosts.value)
  )
  const formattedGrossGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(grossGrossReplacementCosts.value)
  )

  // Calculate days between two dates
  const assessmentDays = computed(() => {
    if (!expertDate.value || !accidentDate.value) return 0

    // Create Date objects from the ISO format dates (yyyy-MM-dd)
    const expertDateObj = new Date(expertDate.value)
    const accidentDateObj = new Date(accidentDate.value)

    // Calculate the difference in milliseconds and convert to days
    const diffTime = expertDateObj.getTime() - accidentDateObj.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  })

  const damageAssessmentDays = computed(() => {
    return Math.max(0, assessmentDays.value)
  })

  // Calculate consideration days based on repair and replacement values
  const considerationDays = computed(() => {
    const rcw = Number(repairCostsWithVat.value)
    const grv = Number(grossResidualValue.value)
    const grp = Number(grossReplacementValue.value)
    if (!grp) return 0
    return rcw + grv <= grp * 1.3 && rcw + grv > netGrossReplacementCosts.value ? 5 : 0
  })

  // Calculate total days
  const totalDays = computed(() => {
    return damageAssessmentDays.value + considerationDays.value
  })

  const totalDaysFromStore = computed(() => {
    return (
      Number(caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAYS)) ||
      0
    )
  })

  const { toast } = useToast()

  // Save all fields
  const handleSaveAll = async () => {
    let payload: Record<string, any> = {
      [DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION]: repairDuration.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT]: repairCostsWithVat.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS]: merantileValueLoss.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE]: grossReplacementValue.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE]: grossResidualValue.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX]: isDifferenzsteuer.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX]: isRegelsteuer.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAYS]: totalDays.value
    }

    // Make sure that the dates are not empty
    if (accidentDate.value) {
      payload[DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_ACCIDENT] = accidentDate.value
    }
    if (expertDate.value) {
      payload[DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_REPORT] = expertDate.value
    }

    await caseStore.bulkUpdateDataWithMapperKeys(payload)

    // Track calculator saved event
    await trackEvent(MetricEventType.CALCULATOR_SAVED, {
      calculator_type: 'Rental Car Compensation',
      calculated_sum: totalDays.value,
      accident_date: accidentDate.value,
      expert_date: expertDate.value,
      repair_duration: repairDuration.value,
      repair_costs_with_vat: repairCostsWithVat.value,
      damage_assessment_days: damageAssessmentDays.value,
      consideration_days: considerationDays.value,
      gross_replacement_value: grossReplacementValue.value,
      gross_residual_value: grossResidualValue.value
    })

    toast({
      title: t('updatedFields'),
      description: 'Rental car fields updated',
      variant: 'default'
    })
    emit('close')
  }
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[70vh] flex-1 space-y-4 overflow-y-auto">
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">{{ t('dates') }}</h3>
          <InputField
            v-model="accidentDate"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_ACCIDENT"
            :label="t('accidentDate')"
            type="date"
            data-cy="rentalCarInput-accidentDate"
          />
          <InputField
            v-model="expertDate"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_DATE_OF_REPORT"
            :label="t('expertDate')"
            type="date"
            data-cy="rentalCarInput-expertDate"
          />
        </div>

        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('repairCosts') }}
          </h3>
          <InputField
            v-model="repairCostsWithVat"
            asterisk
            suffix="€"
            :label="t('grossRepairCosts')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT"
            data-cy="rentalCarInput-grossRepairCosts"
          />
          <InputField
            v-model="formattedRepairCostsWithoutVat"
            suffix="€"
            :label="t('netRepairCosts')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITHOUT_VAT"
            :disabled="true"
            data-cy="rentalCarResult-netRepairCosts"
          />
        </div>

        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('values') }}
          </h3>
          <InputField
            v-model="merantileValueLoss"
            asterisk
            suffix="€"
            :label="t('valueLoss')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS"
            data-cy="rentalCarInput-valueLoss"
          />
          <InputField
            v-model="grossReplacementValue"
            asterisk
            suffix="€"
            :label="t('grossReplacementValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE"
            data-cy="rentalCarInput-grossReplacementValue"
          />
          <InputField
            v-model="formattedNetReplacementValue"
            suffix="€"
            :label="t('netReplacementValue')"
            name="netReplacementValue"
            :disabled="true"
            data-cy="rentalCarResult-netReplacementValue"
          />
          <InputField
            v-model="grossResidualValue"
            asterisk
            suffix="€"
            :label="t('grossResidualValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE"
            data-cy="rentalCarInput-grossResidualValue"
          />
          <InputField
            v-model="formattedNetGrossReplacementCosts"
            suffix="€"
            :label="t('netGrossReplacementCosts')"
            name="netResidualValue"
            :disabled="true"
            data-cy="rentalCarResult-netGrossReplacementCosts"
          />
          <InputField
            v-model="formattedGrossGrossReplacementCosts"
            suffix="€"
            :label="t('grossGrossReplacementCosts')"
            name="grossGrossReplacementCosts"
            :disabled="true"
            data-cy="rentalCarResult-grossGrossReplacementCosts"
          />
          <InputField
            v-model="repairDuration"
            asterisk
            :suffix="t('dayOrDays')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_DURATION"
            :label="t('repairDuration')"
            type="number"
            step="1"
            data-cy="rentalCarInput-repairDuration"
          />
          <InputField
            v-model="damageAssessmentDays"
            :suffix="t('dayOrDays')"
            :name="t('damageAssessmentTime')"
            :label="t('damageAssessmentTime')"
            type="number"
            :disabled="true"
            data-cy="rentalCarResult-damageAssessmentDays"
          />
          <InputField
            v-model="considerationDays"
            :suffix="t('dayOrDays')"
            :name="t('considerationPeriod')"
            :label="t('considerationPeriod')"
            type="number"
            :disabled="true"
            data-cy="rentalCarResult-considerationDays"
          />
        </div>

        <div class="grid grid-cols-1 grid-rows-3 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('tax') }}
          </h3>
          <CheckboxField
            v-model="isDifferenzsteuer"
            class="col-span-2 col-start-1 col-end-3"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX"
            :label="t('differentialTax')"
            data-cy="rentalCarInput-isDifferentialTax"
          />
          <CheckboxField
            v-model="isRegelsteuer"
            class="col-span-2 col-start-1 col-end-3"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX"
            :label="t('regularTax')"
            data-cy="rentalCarInput-isRegularTax"
          />
        </div>
      </div>
    </div>

    <!-- Result -->
    <div class="mt-4">
      <h3 class="text-md font-normal text-gray-900">
        {{ t('compensationForLossOfUse') }}
      </h3>
      <div class="flex items-end gap-4 px-1 pb-2">
        <div class="flex-grow">
          <InputField
            v-model="totalDays"
            :suffix="t('dayOrDays')"
            :name="t('days')"
            type="number"
            :disabled="true"
            data-cy="rentalCarResult-totalDays"
          />
        </div>
        <Button
          :class="[
            'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
            isSavingCase ? 'cursor-not-allowed bg-purple-400' : 'bg-purple-600 hover:bg-purple-700',
            'disabled:cursor-not-allowed disabled:opacity-50'
          ]"
          :disabled="isSavingCase"
          data-cy="rentalCarSaveBtn"
          @click="handleSaveAll"
        >
          <span v-if="isSavingCase">
            <Loader2 class="mr-1 inline h-4 w-4 animate-spin align-text-bottom" />
            {{ t('save') }}
          </span>
          <span v-else>
            {{ t('save') }}
          </span>
        </Button>
      </div>
    </div>
  </div>
</template>
